import type { CarouselItem } from "@/lib/api";
import { clockLabel, elapsedSec, useNowTick } from "@/lib/useNowTick";
import { cn } from "@/lib/utils";

function stepBadge(state?: string): { text: string; className: string } {
  switch (state) {
    case "done":
      return { text: "OK", className: "bg-emerald-100 text-emerald-800" };
    case "active":
      return { text: "teraz", className: "bg-brand/15 text-brand" };
    case "error":
      return { text: "błąd", className: "bg-destructive/15 text-destructive" };
    case "skipped":
      return { text: "pominięto", className: "bg-muted text-muted-foreground" };
    default:
      return { text: "czeka", className: "bg-muted/60 text-muted-foreground" };
  }
}

function logLevelClass(level?: string): string {
  switch (level) {
    case "error":
      return "text-destructive";
    case "ok":
      return "text-emerald-700";
    case "warn":
      return "text-amber-700";
    default:
      return "text-foreground";
  }
}

function formatLogTime(raw?: string): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  const dt = new Date(text);
  if (Number.isNaN(dt.getTime())) return text.slice(11, 19) || text;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}

export default function PublishJobProgress({
  item,
  running,
  error,
}: {
  item?: CarouselItem | null;
  running: boolean;
  error?: string;
}) {
  const progress = item?.publish_progress;
  const phase = progress?.phase || "";
  const active = Boolean(running && phase !== "done" && phase !== "failed" && phase !== "published_partial");
  const now = useNowTick(active);
  const startedAt = progress?.started_at || "";
  const expectedMs = Math.max(8000, Number(progress?.job_expected_ms || progress?.expected_ms || 45_000));
  const elapsed = elapsedSec(startedAt, now);
  const etaSec = Math.max(0, Math.round(expectedMs / 1000) - elapsed);
  const expectedSec = Math.max(1, Math.round(expectedMs / 1000));
  const samples = Number(progress?.samples || 0);
  const percent = Number(
    progress?.percent || (running ? 6 : item?.status === "published" ? 100 : 0),
  );
  const steps = progress?.steps || [];
  const log = progress?.log || [];
  const replaceMode = Boolean(progress?.delete_previous);

  return (
    <div className="space-y-3">
      {replaceMode ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px] text-destructive">
          Tryb replace: najpierw DELETE starego IG/FB, potem świeża publikacja. Pełny dziennik poniżej.
        </p>
      ) : null}
      <div>
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="pr-2">{progress?.detail || (running ? "kolejne kroki IG, potem FB" : "")}</span>
          <span className="shrink-0 font-medium tabular-nums">{percent}%</span>
        </div>
        <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-brand transition-all" style={{ width: `${percent}%` }} />
        </div>
        {startedAt ? (
          <p className="mb-1 text-xs text-muted-foreground">
            minęło {clockLabel(elapsed)}
            {active ? ` · zostało ok. ${clockLabel(etaSec)}` : ""}
            {active ? ` · średnio ~${clockLabel(expectedSec)}` : ""}
            {samples > 0
              ? ` · średnia z ${samples} ${samples === 1 ? "publikacji" : "publikacji"}`
              : ""}
          </p>
        ) : null}
      </div>

      <div>
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Kroki
        </p>
        <ol className="space-y-1.5 text-xs">
          {steps.map((step) => {
            const badge = stepBadge(step.state);
            return (
              <li
                key={step.id}
                className={cn(
                  "rounded-md border px-2 py-1.5",
                  step.state === "active" ? "border-brand/40 bg-brand/5" : "border-border",
                  step.state === "error" ? "border-destructive/40" : "",
                )}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={cn(
                      "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
                      badge.className,
                    )}
                  >
                    {badge.text}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-foreground">{step.label}</div>
                    {step.detail ? (
                      <p
                        className={cn(
                          "mt-0.5 break-words text-[11px] leading-snug",
                          step.state === "error" ? "text-destructive" : "text-muted-foreground",
                        )}
                      >
                        {step.detail}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {log.length ? (
        <div>
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Dziennik ({log.length})
          </p>
          <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-background/80 px-2 py-1.5 font-mono text-[11px] leading-relaxed">
            {log.map((row, idx) => (
              <div
                key={`${row.at || "t"}-${idx}`}
                className={cn("border-b border-border/40 py-1 last:border-0", logLevelClass(row.level))}
              >
                <span className="text-muted-foreground tabular-nums">{formatLogTime(row.at)}</span>
                {row.step_id ? (
                  <span className="text-muted-foreground"> · {row.step_id}</span>
                ) : null}
                <span className="text-muted-foreground"> · </span>
                <span className="break-words">{row.message}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {item?.publish_error ? <p className="text-sm text-destructive">{item.publish_error}</p> : null}
    </div>
  );
}
